/**
 * Dev-only demo harness for the WP7 match screen. Renders the same
 * MatchScreen but with a background fixture pump that feeds snapshots
 * into the WsSnapshotSource — proving the WP6 → SnapshotSource →
 * renderer chain works without a live server.
 *
 * Activated via the hash route `#/demo` in dev builds. Production
 * builds never navigate to this screen (Home has no link).
 */

import { useEffect, useRef, type ReactElement } from "react";
import { toStateHash } from "@pit/protocol";
import { FIXTURE_A, FIXTURE_B } from "../fixtures/snapshots.js";
import { MatchScreen } from "./MatchScreen.js";
import { useWsClient } from "../net/WsClientProvider.js";
import { useStore } from "../store/context.js";
import { handleSnapshotMessage } from "../net/snapshotSource.js";
import { LIVE_MAP_SLUG } from "../render/arenaCatalog.js";

export function DemoMatchScreen(): ReactElement {
  const client = useWsClient();
  const { dispatch } = useStore();
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    // Seed store to look like a live match.
    dispatch({
      type: "connection.status",
      next: {
        kind: "welcomed",
        sessionId: "demo",
        serverVersion: null,
        cleanClose: false,
        testerFlagged: false,
      },
    });
    dispatch({
      type: "match.phase",
      phase: "active",
      winner: null,
      roster: [],
      endsAtTick: null,
      damageDealt: [],
      // #700: the demo harness has no server — it renders the same `bare`
      // geometry it always has (`LIVE_ARENA`'s own default).
      mapSlug: LIVE_MAP_SLUG,
    });
    // Alternate between the two fixture snapshots.
    let tick = 0;
    const push = (): void => {
      const src = tick % 2 === 0 ? FIXTURE_A : FIXTURE_B;
      handleSnapshotMessage(client.snapshotSource, {
        type: "snapshot",
        tick: src.tick,
        stateHash: toStateHash(0),
        state: src,
      });
      dispatch({ type: "hud.sample", snapshot: src });
      tick += 1;
    };
    push();
    timerRef.current = window.setInterval(push, 500);
    return () => {
      if (timerRef.current !== null) {
        window.clearInterval(timerRef.current);
      }
    };
  }, [client, dispatch]);

  return <MatchScreen />;
}
